<script>
import {
  iButton,
  iButtonStyles,
  iButtonTypes,
  iFeaturedIcon,
  iFeaturedIconTypes,
  iFeaturedIconSizes,
} from '@inscribe/signal';

export default {
  name: 'signal-button',
  components: {
    iButton,
    iFeaturedIcon,
  },
  props: {
    title: {
      type: String,
      required: true,
    },
    subtitle: {
      type: String,
      required: true,
    },
    success: {
      type: Boolean,
      default: true,
    },
    warning: {
      type: Boolean,
      default: false,
    },
    hasMenuOpen: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      iButtonStyles,
      iButtonTypes,
      iFeaturedIconTypes,
      iFeaturedIconSizes,
    };
  },
  computed: {
    hasToolsSlot() {
      return !!this.$slots.tools;
    },
  },
  methods: {
    onButtonClick() {
      this.$emit('button-click');
    },
    onMouseOver() {
      this.$emit('mouse-over');
    },
    onMouseLeave() {
      this.$emit('mouse-leave');
    },
  },
};
</script>

<template>
  <button
    data-testid="signal-button"
    :class="{ open: hasMenuOpen }"
    class="signal-button d-flex flex-row justify-content-start align-items-center i-px-6 i-py-5 i-gap-6 i-radius-xl"
    :aria-label="`Document Signal Button: ${title}`"
    data-dd-action-name="document-signal-button"
    @click="onButtonClick"
    @mouseover="onMouseOver"
    @mouseleave="onMouseLeave"
  >
    <i-featured-icon
      :size="iFeaturedIconSizes.SM"
      :type="
        success
          ? iFeaturedIconTypes.SUCCESS
          : warning
            ? iFeaturedIconTypes.WARNING
            : iFeaturedIconTypes.ERROR
      "
      :symbol="
        success
          ? 'medium_solid_check_circle'
          : warning
            ? 'medium_solid_alert_circle'
            : 'medium_solid_alert_triangle'
      "
      :class="{ passed: success, failed: !success, warning: warning }"
      class="signal-icon"
    ></i-featured-icon>
    <div class="text d-flex flex-column align-items-start flex-grow-1 i-gap-2">
      <div class="title i-text-button-3 i-text-neutral-120">
        {{ title }}
      </div>
      <div class="subtitle i-text-body-3 i-text-neutral-110">
        {{ subtitle }}
      </div>
    </div>
    <div
      v-if="hasToolsSlot"
      class="tools on-hover-content i-px-6 i-py-2 i-gap-3"
    >
      <slot name="tools"></slot>
    </div>
    <i-button
      v-else
      class="link-icon on-hover-content"
      :left-icon="'medium-chevron-right'"
      :type="iButtonTypes.NEUTRAL"
      :button-style="iButtonStyles.GHOST"
      @button-click="onButtonClick"
    ></i-button>
  </button>
</template>

<style scoped lang="scss">
.signal-button {
  position: relative;
  background: $light-neutral-default;
  width: 100%;
  border: 0;
  outline: 0;
  transition: background-color 150ms ease-in-out;

  .signal-icon {
    min-width: 36px;
    transition: background-color 150ms ease-in-out;
  }

  .on-hover-content {
    opacity: 0;
    transition:
      opacity,
      background-color 150ms ease-in-out;
    pointer-events: none;
  }

  .text {
    max-width: 340px;

    .title,
    .subtitle {
      text-align: left;
    }
  }

  .tools {
    position: absolute;
    right: 12px;
  }

  &:hover,
  &.open {
    background: $light-neutral-20;

    .signal-icon {
      &.passed {
        background: $light-success-40 !important;
      }

      &.failed {
        background: $light-error-40 !important;
      }

      &.warning {
        background: $light-warning-40 !important;
      }
    }

    .on-hover-content {
      opacity: 1;
      pointer-events: initial;
      background: var(--surface-neutral-subtle);
    }
  }
}
</style>
